import { decodeOptionalOneOf, encodeOneOf, safeDate, safeId, safePage, safeSort, safeText } from '../../../../domain';
import { RestrictedKeys } from '../restricted.key';
import { Places } from '../../../route-consts';
import { SORT_FIELDS } from './collaborator-table';
/** Prefixo dos nomes deste arquivo na intencao - unico na aplicacao (ver `url-codes.test.ts`). */
const PREFIX = 'co';
/** Os parametros deste place: vao para o endereco. */
const P = {
    // Os seis primeiros eram campos de um react-hook-form, perdidos no F5; `sort` e
    // `page` sao novos - a tabela antiga ordena e pagina no cliente, sem deixar
    // rastro no endereco. O detalhe os CONSOME por heranca de `CollaboratorsKeys`.
    Name: `${PREFIX}a`,
    Cpf: `${PREFIX}b`,
    Email: `${PREFIX}c`,
    CompanyId: `${PREFIX}d`,
    MeasurementTypeId: `${PREFIX}e`,
    StatusLevel: `${PREFIX}f`,
    /**
     * O recorte com que os cartoes de Adocao e Engajamento abrem os Colaboradores.
     *
     * Sao filtros da LISTA como os outros - viajam no endereco, sobrevivem ao F5 e
     * voltam com o Voltar -, e nao um modo especial de chegada: quem recebe o link ve
     * exatamente a mesma tela de quem clicou no cartao.
     *
     * `ActivityKind` diz o que conta como atividade; ausente e "medicao ou treino",
     * que e a definicao de ATIVO dos indicadores. Ver `ACTIVITY_KIND_CODES`.
     */
    ActiveFrom: `${PREFIX}g`,
    ActiveTo: `${PREFIX}h`,
    /**
     * Se o periodo de atividade e de PRESENCA ou de AUSENCIA.
     *
     * Ausente e presenca - "registrou algo entre as datas" -, que e como o par de datas
     * sempre foi lido. Ver `ACTIVITY_PRESENCE_CODES` em `CollaboratorsKeys`.
     */
    ActivityPresence: `${PREFIX}i`,
    ActivityKind: `${PREFIX}j`,
    RegisteredFrom: `${PREFIX}k`,
    RegisteredTo: `${PREFIX}l`,
    /**
     * So contas ATIVAS - o que todo indicador de Adocao e Engajamento exige.
     *
     * Viaja junto com os recortes acima, e nao embutido neles: sao duas perguntas, e a
     * lista precisa poder dizer as duas em palavras.
     */
    ActiveAccountsOnly: `${PREFIX}m`,
    /**
     * Se a ficha tem ANAMNESE - os dois lados daquele degrau do funil.
     *
     * Ausente e "tanto faz", que e como a lista sempre se comportou. Ver
     * `ANAMNESE_CODES` em `CollaboratorsKeys`, que e quem publica este nome.
     */
    Anamnese: `${PREFIX}n`,
    Sort: `${PREFIX}o`,
    Page: `${PREFIX}p`,
    /** O periodo da faixa - ver `statusFrom`. (`q` e o atributo `concept_owner`.) */
    StatusFrom: `${PREFIX}r`,
    StatusTo: `${PREFIX}s`
};
/** Os atributos deste place: viajam na intencao, mas nao vao para o endereco. */
const A = {
    /**
     * O presenter do CONCEITO, para a tela de dentro devolver o estado comum a ele.
     *
     * A listagem trabalha sobre uma copia (e dela que sai a assinatura da consulta),
     * mas quem PUBLICA o filtro no endereco e o conceito - dono unico do nome. Sem
     * esta ponte, ordenar a lista mudaria a tela e nao o endereco.
     */
    concept_owner: `${PREFIX}q`
};
/**
 * O codigo de cada faixa no endereco.
 *
 * Mora aqui, e nao em `domain`, porque `StatusLevel` e parametro DESTA tela e de
 * mais nenhuma - e a forma de um parametro no fio e assunto de quem o publica. Ver
 * a nota sobre onde uma tabela de codigo mora, em `domain/index.ts`.
 */
export const STATUS_CODES = {
    '0': 'EXCELLENT',
    '1': 'GOOD',
    '2': 'FAIR',
    '3': 'CRITICAL'
};
/**
 * O codigo de cada tipo de atividade no endereco.
 *
 * `ANY` nao tem codigo de proposito: ele e o padrao, e um parametro escrito para
 * dizer o padrao e ruido num endereco que ja e cifrado.
 */
export const ACTIVITY_KIND_CODES = {
    '1': 'WORKOUT'
};
/** O codigo de cada modo no endereco. A presenca e a ausencia do parametro. */
export const ACTIVITY_PRESENCE_CODES = {
    '1': 'ABSENT',
    '2': 'STALLED'
};
/**
 * O codigo de cada lado da anamnese no endereco.
 *
 * Os dois valores tem codigo - ao contrario de `ACTIVITY_KIND_CODES`, onde o padrao e
 * um dos valores do enum. Aqui o padrao e a AUSENCIA do parametro, entao nenhum dos
 * dois pode ficar sem codigo.
 */
export const ANAMNESE_CODES = {
    '1': 'FILLED',
    '2': 'MISSING'
};
/**
 * Tudo o que a lista de colaboradores pode estar mostrando.
 *
 * Os seis filtros eram campos de um `react-hook-form` no painel antigo - so o
 * `companyId` era lido da URL, e uma vez so, na montagem. `sort` e `page` nao
 * existiam no endereco: a tabela ordena e pagina no cliente, e nada disso
 * sobrevivia a um reload nem ia num link.
 */
export class CollaboratorsKeys extends RestrictedKeys {
    get place() {
        return Places.collaborators;
    }
    // :: filtros
    get name() {
        return safeText(this._intent.getParameterAsString(P.Name));
    }
    set name(value) {
        this._intent.setParameter(P.Name, value ? value : undefined);
    }
    get cpf() {
        return safeText(this._intent.getParameterAsString(P.Cpf));
    }
    set cpf(value) {
        this._intent.setParameter(P.Cpf, value ? value : undefined);
    }
    get email() {
        return safeText(this._intent.getParameterAsString(P.Email));
    }
    set email(value) {
        this._intent.setParameter(P.Email, value ? value : undefined);
    }
    get companyId() {
        return safeId(this._intent.getParameterAsNumber(P.CompanyId));
    }
    set companyId(value) {
        this._intent.setParameter(P.CompanyId, value);
    }
    get measurementTypeId() {
        return safeId(this._intent.getParameterAsNumber(P.MeasurementTypeId));
    }
    set measurementTypeId(value) {
        this._intent.setParameter(P.MeasurementTypeId, value);
    }
    get statusLevel() {
        return decodeOptionalOneOf(this._intent.getParameterAsString(P.StatusLevel), STATUS_CODES);
    }
    set statusLevel(value) {
        this._intent.setParameter(P.StatusLevel, encodeOneOf(value, STATUS_CODES));
    }
    // :: os dois periodos, que vem dos cartoes de Adocao e Engajamento
    /**
     * O recorte de ATIVIDADE - quem registrou algo entre as duas datas.
     *
     * Chega pelos cartoes daquela tela, e nao por um controle desta: o cartao "Ativos
     * (30 dias)" abre esta lista com a janela dele, e o numero de um lado e o do outro.
     * A regra esta em `PatientSpecification.activeBetween`, no servidor.
     */
    get activeFrom() {
        return safeDate(this._intent.getParameterAsString(P.ActiveFrom));
    }
    set activeFrom(value) {
        this._intent.setParameter(P.ActiveFrom, value);
    }
    get activeTo() {
        return safeDate(this._intent.getParameterAsString(P.ActiveTo));
    }
    set activeTo(value) {
        this._intent.setParameter(P.ActiveTo, value);
    }
    /**
     * Se o periodo acima e de PRESENCA ou de AUSENCIA - ver `ActivityPresence`.
     *
     * Um modo, e nao um segundo par de datas: a pergunta e sobre o MESMO intervalo, e
     * duas datas a mais no endereco para dizer as mesmas duas seria um par que pode
     * discordar do outro.
     */
    get activityPresence() {
        return decodeOptionalOneOf(this._intent.getParameterAsString(P.ActivityPresence), ACTIVITY_PRESENCE_CODES);
    }
    set activityPresence(value) {
        this._intent.setParameter(P.ActivityPresence, encodeOneOf(value, ACTIVITY_PRESENCE_CODES));
    }
    get activityKind() {
        return decodeOptionalOneOf(this._intent.getParameterAsString(P.ActivityKind), ACTIVITY_KIND_CODES);
    }
    set activityKind(value) {
        this._intent.setParameter(P.ActivityKind, encodeOneOf(value, ACTIVITY_KIND_CODES));
    }
    /** O recorte de CADASTRO - quem entrou na base entre as duas datas. */
    get registeredFrom() {
        return safeDate(this._intent.getParameterAsString(P.RegisteredFrom));
    }
    set registeredFrom(value) {
        this._intent.setParameter(P.RegisteredFrom, value);
    }
    get registeredTo() {
        return safeDate(this._intent.getParameterAsString(P.RegisteredTo));
    }
    set registeredTo(value) {
        this._intent.setParameter(P.RegisteredTo, value);
    }
    /**
     * O periodo em que se procura a ultima medicao do filtro de FAIXA.
     *
     * Chega da barra do Indice de saude geral, que conta a faixa da ultima medicao DENTRO
     * do periodo do dashboard. Sem ele a lista olhava a ultima de todas, e quem mediu de
     * novo depois do periodo caia numa faixa na barra e em outra na lista (08/10/2026).
     * Ausente e "a ultima de todas", como a lista sempre se comportou.
     */
    get statusFrom() {
        return safeDate(this._intent.getParameterAsString(P.StatusFrom));
    }
    set statusFrom(value) {
        this._intent.setParameter(P.StatusFrom, value);
    }
    get statusTo() {
        return safeDate(this._intent.getParameterAsString(P.StatusTo));
    }
    set statusTo(value) {
        this._intent.setParameter(P.StatusTo, value);
    }
    /**
     * So contas ATIVAS.
     *
     * Ausente e "todas", que e como esta lista sempre se comportou; `1` liga o recorte,
     * e quem o liga sao os cartoes de Adocao e Engajamento - la toda contagem de
     * pessoas exige conta ativa, e sem isto o numero do cartao e o da lista divergiam
     * por quem foi desativado e continuou registrando.
     */
    get activeAccountsOnly() {
        return this._intent.getParameterAsString(P.ActiveAccountsOnly) === '1';
    }
    set activeAccountsOnly(value) {
        this._intent.setParameter(P.ActiveAccountsOnly, value ? '1' : undefined);
    }
    /**
     * Se a ficha tem ANAMNESE preenchida.
     *
     * Chega dos dois lados daquele degrau em Adocao e Engajamento - o degrau
     * "Preencheram a anamnese" manda `FILLED`, e o cartao de lacuna "Sem anamnese"
     * manda `MISSING`. Tambem e escolha desta tela, no combo ANAMNESE.
     */
    get anamnese() {
        return decodeOptionalOneOf(this._intent.getParameterAsString(P.Anamnese), ANAMNESE_CODES);
    }
    set anamnese(value) {
        this._intent.setParameter(P.Anamnese, encodeOneOf(value, ANAMNESE_CODES));
    }
    // :: ordenacao
    /**
     * A ordenacao como uma string: `nome` crescente, `-nome` decrescente.
     *
     * Um parametro e nao dois, porque e uma decisao so - e porque um link deve ler
     * como uma frase, nao como um formulario submetido.
     */
    get sort() {
        return safeSort(this._intent.getParameterAsString(P.Sort), SORT_FIELDS);
    }
    set sort(value) {
        this._intent.setParameter(P.Sort, value ? value : undefined);
    }
    // :: pagina
    get page() {
        return safePage(this._intent.getParameterAsNumber(P.Page));
    }
    set page(value) {
        this._intent.setParameter(P.Page, value > 1 ? value : undefined);
    }
    // :: o dono do estado comum
    /** O presenter do conceito - ver `A.concept_owner`. */
    get concept() {
        return this._intent.attributes.get(A.concept_owner);
    }
    set concept(value) {
        this._intent.attributes.set(A.concept_owner, value);
    }
}
//# sourceMappingURL=collaborators.key.js.map